import type {
  HiddenOptionsAndValues,
  Monogram,
  Option,
  OptionsSelection,
  StylePreset,
} from '@susu/custom-made/utils/interfaces';
import type { ConfigurationTypes } from '@susu/custom-made/utils/productDefinition';

export enum actions {
  SELECT_OPTION = 'SELECT_OPTION',
  ADD_MONOGRAM = 'ADD_MONOGRAM',
  DELETE_MONOGRAM = 'DELETE_MONOGRAM',
  SELECT_BASE_STYLE = 'SELECT_BASE_STYLE',
  REMOVE_BASE_STYLE = 'REMOVE_BASE_STYLE',
  ADD_REMOVE_ADDITIONAL_PRODUCT = 'ADD_REMOVE_ADDITIONAL_PRODUCT',
  CHANGE_BASE_STYLE_STATUS = 'CHANGE_BASE_STYLE_STATUS',
  CLEAR_MONOGRAMS = 'CLEAR_MONOGRAMS',
  SET_SELECTED_OPTION = 'SET_SELECTED_OPTION',
  SET_SELECTED_OPTION_GROUP = 'SET_SELECTED_OPTION_GROUP',
  SET_ANIMATION_STATUS = 'SET_ANIMATION_STATUS',
  SET_HIDDEN_OPTIONS_AND_VALUES = 'SET_HIDDEN_OPTIONS_AND_VALUES',
  SET_IS_EXTRA_PRODUCT_ADDED_MODAL_SHOWN = 'SET_IS_EXTRA_PRODUCT_ADDED_MODAL_SHOWN',
}

export interface OptionStateProps {
  selectedOptions: OptionsSelection;
  selectedMonogram: Monogram;
  selectedBaseStyle: Record<string, StylePreset>;
  isAdditionalProductAdded: Record<string, boolean>;
  isBaseStyleSelection: boolean;
  selectedOption: Option | undefined;
  selectedOptionGroup: Option | undefined;
  animation: { status: boolean; from: Option | undefined; to: Option | undefined };
  hiddenOptionsAndValues: HiddenOptionsAndValues;
  isExtraProductAddedModalShown: boolean;
}

export const initialState: OptionStateProps = {
  selectedOptions: {},
  selectedMonogram: {},
  selectedBaseStyle: {},
  isAdditionalProductAdded: {},
  isBaseStyleSelection: false,
  selectedOption: undefined,
  selectedOptionGroup: undefined,
  animation: { status: false, from: undefined, to: undefined },
  hiddenOptionsAndValues: {
    optionsPerConfiguration: {},
    optionValuesPerConfiguration: {},
    monogramColors: [],
    monogramPositions: [],
  },
  isExtraProductAddedModalShown: false,
};

interface SelectOptionAction {
  type: actions.SELECT_OPTION;
  payload: OptionsSelection;
}

interface SelectMonogramAction {
  type: actions.ADD_MONOGRAM;
  payload: Monogram;
}

interface DeleteMonogramAction {
  type: actions.DELETE_MONOGRAM;
  payload: number;
}

interface SelectBaseStyleAction {
  type: actions.SELECT_BASE_STYLE;
  payload: {
    configurationType: ConfigurationTypes;
    baseStyle: StylePreset;
  };
}

interface RemoveBaseStyleAction {
  type: actions.REMOVE_BASE_STYLE;
  payload: ConfigurationTypes;
}

interface AddRemoveAdditionalProductAction {
  type: actions.ADD_REMOVE_ADDITIONAL_PRODUCT;
  payload: {
    configurationType: ConfigurationTypes;
    isAdded: boolean;
  };
}

interface ChangeBaseStyleStatus {
  type: actions.CHANGE_BASE_STYLE_STATUS;
  payload: boolean;
}

interface ClearMonogramsAction {
  type: actions.CLEAR_MONOGRAMS;
}

interface SetSelectedOptionAction {
  type: actions.SET_SELECTED_OPTION;
  payload: Option | undefined;
}

interface SetSelectedOptionGroupAction {
  type: actions.SET_SELECTED_OPTION_GROUP;
  payload: Option | undefined;
}

interface SetAnimationstatusAction {
  type: actions.SET_ANIMATION_STATUS;
  payload: { status: boolean; from: Option | undefined; to: Option | undefined };
}
interface SetHiddenOptionsAndValues {
  type: actions.SET_HIDDEN_OPTIONS_AND_VALUES;
  payload: HiddenOptionsAndValues;
}

interface SetIsExtraProductAddedModalShown {
  type: actions.SET_IS_EXTRA_PRODUCT_ADDED_MODAL_SHOWN;
  payload: boolean;
}

export type OptionActionTypes =
  | SelectOptionAction
  | SelectMonogramAction
  | DeleteMonogramAction
  | SelectBaseStyleAction
  | RemoveBaseStyleAction
  | AddRemoveAdditionalProductAction
  | ChangeBaseStyleStatus
  | ClearMonogramsAction
  | SetSelectedOptionAction
  | SetSelectedOptionGroupAction
  | SetAnimationstatusAction
  | SetHiddenOptionsAndValues
  | SetIsExtraProductAddedModalShown;

export default function OptionReducer(state: OptionStateProps, action: OptionActionTypes) {
  switch (action.type) {
    case actions.SELECT_OPTION:
      return {
        ...state,
        selectedOptions: {
          ...state.selectedOptions,
          ...action.payload,
        },
      };
    case actions.SET_HIDDEN_OPTIONS_AND_VALUES:
      return {
        ...state,
        hiddenOptionsAndValues: {
          ...state.hiddenOptionsAndValues,
          ...action.payload,
        },
      };
    case actions.ADD_MONOGRAM:
      return {
        ...state,
        selectedMonogram: { ...state.selectedMonogram, ...action.payload },
      };
    case actions.DELETE_MONOGRAM: {
      const tmpMonogramObj = { ...state.selectedMonogram };
      delete tmpMonogramObj[action.payload];
      return {
        ...state,
        selectedMonogram: tmpMonogramObj,
      };
    }
    case actions.SELECT_BASE_STYLE:
      return {
        ...state,
        selectedBaseStyle: {
          ...state.selectedBaseStyle,
          [action.payload.configurationType]: action.payload.baseStyle,
        },
      };
    case actions.REMOVE_BASE_STYLE: {
      const baseStyleSelection = { ...state.selectedBaseStyle };
      delete baseStyleSelection[action.payload];
      return {
        ...state,
        selectedBaseStyle: {
          baseStyleSelection,
        },
      };
    }

    case actions.ADD_REMOVE_ADDITIONAL_PRODUCT:
      return {
        ...state,
        isAdditionalProductAdded: {
          ...state.isAdditionalProductAdded,
          [action.payload.configurationType]: action.payload.isAdded,
        },
      };
    case actions.CHANGE_BASE_STYLE_STATUS:
      return { ...state, isBaseStyleSelection: action.payload };
    case actions.CLEAR_MONOGRAMS:
      return {
        ...state,
        selectedMonogram: {},
      };
    case actions.SET_SELECTED_OPTION:
      return {
        ...state,
        selectedOption: action.payload,
      };
    case actions.SET_SELECTED_OPTION_GROUP:
      return {
        ...state,
        selectedOptionGroup: action.payload,
      };
    case actions.SET_ANIMATION_STATUS:
      return {
        ...state,
        animation: action.payload,
      };
    case actions.SET_IS_EXTRA_PRODUCT_ADDED_MODAL_SHOWN:
      return {
        ...state,
        isExtraProductAddedModalShown: action.payload,
      };
    default:
      return state;
  }
}
